import type { Countries } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import { required } from '@ps/web-shared-validators';
import { isArray } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useField, useForm } from 'react-final-form';

import { Country } from '@swf/core/enums';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import countriesJson from '@swf/generated/countries.json';

import useDisableField from '../../../../../../hooks/useDisableField';
import type { ProvinceCity } from '../../../../../../types/Cashier';
import { getCountryStates } from '../../../../../../utils/country-states/country-states';
import City from '../../../../../City/City';
import RMFDropDown from '../../../RMFDropDown/RMFDropDown';

import {
  TXTCLI_Please_provide_province_of_bir_ELL,
  TXTWCL_City_of_birth,
  TXTWCL_Country_of_birth,
  TXTWCL_Please_provide_city_of_birth,
  TXTWCL_Please_select_your_city_of_bir_ELL,
  TXTWCL_Province_of_birth,
  TXTWCL_Select_city_of_birth,
  TXTWCL_Select_country_of_birth,
  TXTWCL_Select_province_of_birth
} from './PlaceOfBirthFieldsIT.messages.json';
import { label, wrapper } from './PlaceOfBirthFieldsIT.scss';

const countries: Countries = countriesJson;

const getProvinceCities = (provinceCities: ProvinceCity[]) => {
  const provinces = [] as string[];
  const cities: Record<string, string[]> = {};

  provinceCities.forEach((provinceCity) => {
    const { province } = provinceCity;
    if (!cities[province]) {
      cities[province] = [];
      provinces.push(province);
    }

    cities[province].push(provinceCity.city);
  });

  return {
    provinces,
    cities
  };
};

const countryOfBirthField = 'countryOfBirth';
const stateOfBirthField = 'stateOfBirth';
const cityOfBirthField = 'cityOfBirth';

const PlaceOfBirthFieldsIT = () => {
  const form = useForm();
  const disableField = useDisableField();
  const [city, setCity] = useState<string[]>();
  const {
    data: {
      italyBirthPlace: { countries: italyCountryOfBirth, provinceCities }
    }
  } = useWizardContext();

  const provinceCityData = getProvinceCities(provinceCities);
  const defaultCountry = !italyCountryOfBirth.length
    ? Country.Italy
    : italyCountryOfBirth.length === 1
      ? italyCountryOfBirth[0].countryCode
      : undefined;

  const {
    input: { value: countryOfBirthValue }
  } = useField(countryOfBirthField, {
    ...(!disableField(countryOfBirthField) && { initialValue: defaultCountry }),
    subscription: { value: true }
  });
  const {
    input: { value: stateOfBirthValue }
  } = useField(stateOfBirthField, {
    ...(!disableField(stateOfBirthField) && {
      initialValue:
        provinceCityData.provinces.length === 1 ? provinceCityData.provinces[0] : undefined
    }),
    subscription: { value: true }
  });
  useField(cityOfBirthField, {
    ...(!disableField(cityOfBirthField) && {
      initialValue: provinceCities.length === 1 ? provinceCities[0].city : undefined
    })
  });

  useEffect(() => {
    const cities = provinceCityData.cities[stateOfBirthValue];

    if (!cities) {
      setCity([]);
      return;
    }

    if (cities.length === 1) {
      form.change(cityOfBirthField, cities[0]);
      setCity([cities[0]]);
    } else {
      setCity(cities);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateOfBirthValue]);

  const getCountryOptions = () => {
    let countryOptions = [];

    if (italyCountryOfBirth.length) {
      for (const birthCountry of italyCountryOfBirth) {
        const translation = countries.find((c) => c.value === birthCountry.countryCode);
        if (translation) {
          countryOptions.push(translation);
        } else {
          countryOptions.push({
            value: birthCountry.countryCode,
            text: birthCountry.countryName
          });
        }
      }
    } else {
      countryOptions = countries.filter((country) => country.value === Country.Italy);
    }

    return countryOptions;
  };

  const getStateOptions = () => {
    const options = getCountryStates(countryOfBirthValue);

    if (provinceCities.length) {
      return options.filter((option) => provinceCityData.provinces.includes(option.value));
    } else {
      return options;
    }
  };

  const stateOptions = getStateOptions();

  const getCityOptions = () => {
    let cityOptions = [] as { value: string; text: string }[];
    if (
      isArray(city) &&
      city.length &&
      !disableField(cityOfBirthField) &&
      stateOptions.length > 1
    ) {
      cityOptions = city.map((item) => ({
        text: item,
        value: item
      }));
    } else if (provinceCityData.cities[stateOfBirthValue]) {
      cityOptions = provinceCityData.cities[stateOfBirthValue].map((item: string) => ({
        text: item,
        value: item
      }));
    }
    return cityOptions;
  };

  const resetFormState = () => {
    form.change(stateOfBirthField);
  };

  const countryOptions = getCountryOptions();
  const cityOptions = getCityOptions();

  return (
    <div className={wrapper}>
      <RMFDropDown
        id={countryOfBirthField}
        label={TXTWCL_Country_of_birth}
        options={countryOptions}
        placeholder={<Translation {...TXTWCL_Select_country_of_birth} />}
        disabled={countryOptions.length < 2}
        {...(countryOptions.length > 1 && { onSelect: resetFormState })}
      />
      {!!stateOptions.length && (
        <RMFDropDown
          id={stateOfBirthField}
          label={TXTWCL_Province_of_birth}
          options={stateOptions}
          validators={[required(TXTCLI_Please_provide_province_of_bir_ELL)]}
          placeholder={<Translation {...TXTWCL_Select_province_of_birth} />}
          disabled={stateOptions.length < 2}
        />
      )}
      {cityOptions.length ? (
        <RMFDropDown
          id={cityOfBirthField}
          label={TXTWCL_City_of_birth}
          options={cityOptions}
          validators={[required(TXTWCL_Please_select_your_city_of_bir_ELL)]}
          placeholder={<Translation {...TXTWCL_Select_city_of_birth} />}
          disabled={cityOptions.length < 2}
        />
      ) : (
        <City
          fieldName={cityOfBirthField}
          requiredMessage={TXTWCL_Please_provide_city_of_birth}
          label={TXTWCL_City_of_birth}
          className={label}
        />
      )}
    </div>
  );
};

export default PlaceOfBirthFieldsIT;
